import React, { useState, useEffect } from 'react';
import { Droplet, Gauge, Trash2, History } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import toast from 'react-hot-toast';
import EditableTextCell from '../Presupuestos/EditableTextCell';
import { Modal } from '../common';
import ConfirmDeleteModal from '../Presupuestos/ConfirmDeleteModal';

const fmtKm = (val) => Number(val).toLocaleString('es-VE');

const VehiculoHistorialModal = ({ isOpen, onClose, vehiculo, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [cambios, setCambios] = useState(null);
  const [registros, setRegistros] = useState(null);
  const [vehiculoActual, setVehiculoActual] = useState(vehiculo);
  // { tipo: 'cambio' | 'registro', id }
  const [porEliminar, setPorEliminar] = useState(null);

  useEffect(() => {
    if (isOpen && vehiculo) {
      setCambios(null);
      setRegistros(null);
      setVehiculoActual(vehiculo);
      cargar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vehiculo?.id]);

  const cargar = async (conSpinner = true) => {
    if (conSpinner) setLoading(true);
    try {
      const [cambiosRes, registrosRes] = await Promise.all([
        axiosInstance.get(`/api/equipos-moviles/vehiculos/${vehiculo.id}/cambio-aceite`),
        axiosInstance.get(`/api/equipos-moviles/vehiculos/${vehiculo.id}/km`),
      ]);
      setCambios(cambiosRes.data);
      setRegistros(registrosRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  // El backend reencadena los KM y recalcula la base del aceite, asi que cualquier
  // mutacion puede mover filas vecinas: se relee en vez de parchear solo la fila tocada.
  const aplicarCambio = async (vehiculoActualizado) => {
    if (vehiculoActualizado) setVehiculoActual(vehiculoActualizado);
    await cargar(false);
    if (onChange) onChange();
  };

  const handleUpdateCambio = async (id, field, value) => {
    try {
      const res = await axiosInstance.put(`/api/equipos-moviles/vehiculos/${vehiculo.id}/cambio-aceite/${id}`, { [field]: value });
      await aplicarCambio(res.data.vehiculo);
      toast.success('Actualizado');
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Error al actualizar');
      return false;
    }
  };

  const handleDeleteCambio = async (id) => {
    try {
      const res = await axiosInstance.delete(`/api/equipos-moviles/vehiculos/${vehiculo.id}/cambio-aceite/${id}`);
      await aplicarCambio(res.data.vehiculo);
      toast.success('Eliminado');
    } catch (error) {
      console.error(error);
      toast.error('Error al eliminar');
    } finally {
      setPorEliminar(null);
    }
  };

  const handleUpdateRegistro = async (id, field, value) => {
    try {
      const res = await axiosInstance.put(`/api/equipos-moviles/vehiculos/${vehiculo.id}/km/${id}`, { [field]: value });
      await aplicarCambio(res.data.vehiculo);
      toast.success('Actualizado');
      return true;
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'Error al actualizar');
      return false;
    }
  };

  const handleDeleteRegistro = async (id) => {
    try {
      const res = await axiosInstance.delete(`/api/equipos-moviles/vehiculos/${vehiculo.id}/km/${id}`);
      await aplicarCambio(res.data.vehiculo);
      toast.success('Eliminado');
    } catch (error) {
      console.error(error);
      toast.error('Error al eliminar');
    } finally {
      setPorEliminar(null);
    }
  };

  if (!vehiculo) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-sura-blue to-sura-blue-light rounded-xl text-white shadow-lg shadow-sura-blue/20">
            <History size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{vehiculoActual?.nombre}</h3>
            <p className="text-xs text-gray-500 font-normal font-mono">{fmtKm(vehiculoActual?.kilometraje_actual)} km actuales</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sura-blue"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Cambios de aceite */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Droplet size={17} className="text-amber-500" />
                <h4 className="text-sm font-bold uppercase tracking-wide text-gray-500">Cambios de Aceite</h4>
                <span className="text-xs text-gray-400">({cambios?.length || 0})</span>
              </div>
              {!cambios || cambios.length === 0 ? (
                <p className="text-sm text-gray-400 italic px-1">Aún no se ha registrado ningún cambio de aceite para este vehículo.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-amber-100">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '22%' }} />
                      <col />
                      <col style={{ width: '2.5rem' }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-amber-50 text-amber-700 text-xs uppercase tracking-wide">
                        <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                        <th className="text-right px-4 py-3 font-semibold">Kilometraje</th>
                        <th className="text-left px-4 py-3 font-semibold">Observación</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cambios.map(c => (
                        <tr key={c.id} className="border-t border-amber-50 group">
                          <td className="px-4 py-3 text-gray-700 font-mono text-sm">
                            <EditableTextCell type="date" value={c.fecha} onSave={(val) => handleUpdateCambio(c.id, 'fecha', val)} />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-800 font-mono">
                            <EditableTextCell type="number" value={c.kilometraje} onSave={(val) => handleUpdateCambio(c.id, 'kilometraje', val)} />
                            <span className="text-gray-400 font-normal"> km</span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 truncate">
                            <EditableTextCell value={c.observacion} placeholder="Sin observación" onSave={(val) => handleUpdateCambio(c.id, 'observacion', val)} />
                          </td>
                          <td className="px-2 py-3">
                            <button
                              onClick={() => setPorEliminar({ tipo: 'cambio', id: c.id })}
                              className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Historial de KM */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Gauge size={17} className="text-sura-blue" />
                <h4 className="text-sm font-bold uppercase tracking-wide text-gray-500">Historial de Kilometraje</h4>
                <span className="text-xs text-gray-400">({registros?.length || 0}{registros?.length === 60 ? '+' : ''})</span>
              </div>
              {!registros || registros.length === 0 ? (
                <p className="text-sm text-gray-400 italic px-1">Sin registros de kilometraje.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-blue-100">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col style={{ width: '11%' }} />
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '12%' }} />
                      <col />
                      <col style={{ width: '2.5rem' }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-sura-blue/5 text-sura-blue text-xs uppercase tracking-wide">
                        <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                        <th className="text-left px-4 py-3 font-semibold">Conductor</th>
                        <th className="text-right px-4 py-3 font-semibold">KM Inicial</th>
                        <th className="text-right px-4 py-3 font-semibold">KM Final</th>
                        <th className="text-right px-4 py-3 font-semibold">Recorrido</th>
                        <th className="text-left px-4 py-3 font-semibold">Observación</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {registros.map(r => (
                        <tr key={r.id} className="border-t border-blue-50 group">
                          <td className="px-4 py-3 text-gray-700 font-mono text-sm">
                            <EditableTextCell type="date" value={r.fecha} onSave={(val) => handleUpdateRegistro(r.id, 'fecha', val)} />
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-sm truncate">
                            <EditableTextCell value={r.usuario} placeholder="—" onSave={(val) => handleUpdateRegistro(r.id, 'usuario', val)} />
                          </td>
                          <td className="px-4 py-3 text-right text-gray-400 font-mono text-sm">{fmtKm(r.km_inicial)}</td>
                          <td className="px-4 py-3 text-right text-gray-600 font-mono text-sm">
                            <EditableTextCell type="number" value={r.km_final} onSave={(val) => handleUpdateRegistro(r.id, 'km_final', val)} />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-800 font-mono text-sm">{fmtKm(r.km_recorrido)}</td>
                          <td className="px-4 py-3 text-gray-600 text-sm truncate">
                            <EditableTextCell value={r.observacion} placeholder="Sin novedad" onSave={(val) => handleUpdateRegistro(r.id, 'observacion', val)} />
                          </td>
                          <td className="px-2 py-3">
                            <button
                              onClick={() => setPorEliminar({ tipo: 'registro', id: r.id })}
                              className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-end w-full">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cerrar
          </button>
        </div>
      </Modal.Footer>

      <ConfirmDeleteModal
        isOpen={porEliminar !== null}
        onClose={() => setPorEliminar(null)}
        onConfirm={() => (porEliminar.tipo === 'cambio'
          ? handleDeleteCambio(porEliminar.id)
          : handleDeleteRegistro(porEliminar.id))}
        title={porEliminar?.tipo === 'cambio' ? 'Eliminar cambio de aceite' : 'Eliminar registro de kilometraje'}
        message={porEliminar?.tipo === 'cambio'
          ? '¿Seguro que deseas eliminar este cambio de aceite? La base del próximo mantenimiento vuelve al cambio anterior.'
          : '¿Seguro que deseas eliminar este registro? El kilometraje del vehículo se recalcula sin él.'}
      />
    </Modal>
  );
};

export default VehiculoHistorialModal;
